import { MakePayout } from "@/routes/(app)/_main-layout/_balances/-components/payout-form";
import { EU } from "@/components/svg/eu";
import { Gbp } from "@/components/svg/gbp";
import { numberToDecimalWithCurrency } from "@/lib/utils";
import { createFileRoute, Link, useRouteContext } from "@tanstack/react-router";
import { AuthorityRequired } from "@/lib/auth";
import { Spinner } from "@/components/ui/spinner";
import { Calendar, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CardWithHeader } from "@/components/card-with-header";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  formatDayToAbbreviation,
  ScheduleSettings,
  days as daysArray,
} from "@/routes/(app)/_main-layout/_balances/-components/scheduled-payouts/schedule-settings";
import { PayinAccountsWithAggregatedBalancesResponse } from "@/types/payin-accounts-with-aggregated-balances";
import { useState } from "react";
import { payinAccountsWithAggregatedBalancesQuery } from "@/lib/queries";
import { useQuery } from "@tanstack/react-query";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import { PayeeDetailsPerCurrency } from "@/types/merchant";

export const Route = createFileRoute(
  "/(app)/_main-layout/_balances/balances/overview",
)({
  component: () => <BalancesComponent />,
  loader: async ({ context }) => {
    return context.queryClient.ensureQueryData({
      ...payinAccountsWithAggregatedBalancesQuery(
        context.merchantId,
        context.accessToken,
        context.selectedApplicationId,
      ),
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-[120px] items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white p-6">
      <Spinner width={20} height={20} />
      <p className="text-sm text-slate-500">Loading balances…</p>
    </div>
  ),
});

function formatBalance(balance: number | null, currency: string): string {
  if (balance === null) return "—";
  return numberToDecimalWithCurrency(balance, currency);
}

const BalancesComponent = () => {
  const context = useRouteContext({
    from: "/(app)/_main-layout/_balances/balances/overview",
  });
  const { data: payinAccounts, isLoading } = useQuery(
    payinAccountsWithAggregatedBalancesQuery(
      context.merchantId,
      context.accessToken,
      context.selectedApplicationId,
    ),
  );

  if (isLoading || !payinAccounts) {
    return (
      <div className="flex min-h-[120px] items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white p-6">
        <Spinner width={20} height={20} />
        <p className="text-sm text-slate-500">Loading balances…</p>
      </div>
    );
  }

  const allAccounts = payinAccounts.flatMap((group) =>
    group.accounts.map((account) => ({
      ...account,
      currencyGroup: group.currency,
    })),
  );

  const sortedAccounts = allAccounts
    .slice()
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));

  return (
    <div className="space-y-4">
      {sortedAccounts.map((account) => (
        <div
          key={`${account.forCurrency}-${account.id}`}
          data-testid={`account-${account.forCurrency}-${account.id}`}
        >
          <AccountCard account={account} />
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Account Card                                                       */
/* ------------------------------------------------------------------ */

const AccountCard = ({
  account,
}: {
  account: PayinAccountsWithAggregatedBalancesResponse["accounts"][number];
}) => {
  const context = useRouteContext({
    from: "/(app)/_main-layout/_balances/balances/overview",
  });
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const selectedApplication = useSelectedApplication();
  const vaProviderType = selectedApplication.payeeDetailsPerCurrency?.find(
    (payee) => payee.id === account.id,
  )?.vaProviderType;

  const hasScheduleFeature = scheduledPayoutsEnabledForMerchant({
    account,
    vaProviderType,
  });
  const canPayout =
    account.isVirtualAccount &&
    !account.isLimitedVirtualAccount &&
    account.virtualAccountInformation.availableBalance !== null &&
    account.virtualAccountInformation.balance !== null;

  const currencyFlag =
    account.forCurrency === "EUR" ? (
      <EU width="18" height="18" />
    ) : (
      <Gbp width={18} height={18} />
    );

  const accountDetails = account.accountIdentification2
    ? `${account.accountIdentification1.number} · ${account.accountIdentification2.number}`
    : account.accountIdentification1.number;

  return (
    <CardWithHeader
      variant="exchange"
      label={
        <>
          {currencyFlag} {account.forCurrency}
        </>
      }
      title={account.accountHolderName}
      fullHeight={false}
      noChildPadding
      headerContent={
        <div className="flex items-center gap-3">
          {canPayout ? (
            <AuthorityRequired
              authoritiesRequired={["INITIATE_PAYOUTS"]}
              authAuthorities={context.authorities}
            >
              <MakePayout payInAccount={account} />
            </AuthorityRequired>
          ) : null}
        </div>
      }
    >
      {/* Balances — equal halves */}
      <div className="grid grid-cols-2 divide-x divide-slate-100 px-5 py-3">
        <div className="min-w-0 pr-5">
          <div className="text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
            {formatBalance(
              account.virtualAccountInformation.balance,
              account.forCurrency,
            )}
          </div>
          <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-400">
            Total Balance
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            All funds held, including pending transactions.
          </div>
        </div>

        <div className="min-w-0 pl-5">
          <div className="text-2xl font-semibold tabular-nums tracking-tight text-slate-700">
            {formatBalance(
              account.virtualAccountInformation.availableBalance,
              account.forCurrency,
            )}
          </div>
          <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-400">
            Available for Payout
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            Funds you can pay out now.
          </div>
        </div>
      </div>

      {/* Account reference */}
      <div className="border-t border-slate-100 px-5 py-1.5">
        <span className="font-mono text-[11px] text-slate-400">
          {accountDetails}
        </span>
      </div>

      {/* Schedule footer */}
      {hasScheduleFeature ? (
        <ScheduleFooter
          account={account}
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
        />
      ) : null}
    </CardWithHeader>
  );
};

/* ------------------------------------------------------------------ */
/*  Schedule Footer                                                    */
/* ------------------------------------------------------------------ */

const ScheduleFooter = ({
  account,
  open,
  onOpenChange,
}: {
  account: PayinAccountsWithAggregatedBalancesResponse["accounts"][number];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const formatSchedule = (
    schedule: PayinAccountsWithAggregatedBalancesResponse["accounts"][number]["payoutSchedule"],
  ) => {
    if (!schedule) return null;
    const days = schedule.days
      ?.map((d) => formatDayToAbbreviation(d))
      .sort((a, b) => {
        const dayOrder = daysArray.map((day) => day.label);
        return dayOrder.indexOf(a || "") - dayOrder.indexOf(b || "");
      })
      .join(", ");
    return `${days} at ${schedule.hour}:00`;
  };

  return (
    <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/70 px-5 py-2">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        {account.payoutSchedule ? (
          <>
            <span>Automatic payouts</span>
            <Badge
              variant="outline"
              className="border-slate-200 font-mono text-[11px] font-normal text-slate-600"
            >
              <Calendar className="mr-1 h-3 w-3" />
              {formatSchedule(account.payoutSchedule)}
            </Badge>
          </>
        ) : (
          <span className="text-slate-400">No automatic payouts</span>
        )}
      </div>
      {/* A schedule pays the default destination account, so one must exist before a schedule can
          be created. An existing schedule stays editable so it can still be switched off. */}
      {!account.payoutSchedule && !hasDefaultDestinationAccount(account) ? (
        <Link
          to="/settings/accounts"
          className="text-xs font-medium text-volume-text-purple hover:underline"
          data-testid="schedule-footer-choose-destination"
        >
          Choose a destination account to set up
        </Link>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="text-xs font-medium text-volume-text-purple hover:underline"
            >
              {account.payoutSchedule ? "Edit" : "Set up"}
            </button>
          </DialogTrigger>
          <DialogContent className="max-h-[min(90dvh,880px)] w-[calc(100vw-1.5rem)] max-w-4xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/70 sm:rounded-3xl">
            <DialogHeader>
              <DialogTitle>
                {account.payoutSchedule
                  ? "Edit Payout Schedule"
                  : "Set up automatic payouts"}
              </DialogTitle>
            </DialogHeader>
            <ScheduleSettings account={account} setDialogOpen={onOpenChange} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Utilities                                                          */
/* ------------------------------------------------------------------ */

// Mirrors the backend allow-list in ExecuteScheduledPayoutUseCase: a scheduled payout on any other
// provider is refused when it runs, so offering the schedule would only produce failed payouts.
const PROVIDERS_SUPPORTING_SCHEDULED_PAYOUTS: readonly PayeeDetailsPerCurrency["vaProviderType"][] =
  ["OPTIMUS", "CLEARBANK"];

export const scheduledPayoutsEnabledForMerchant = ({
  account,
  vaProviderType,
}: {
  account: PayinAccountsWithAggregatedBalancesResponse["accounts"][number];
  vaProviderType: PayeeDetailsPerCurrency["vaProviderType"] | undefined;
}) => {
  if (!account.isVirtualAccount) return false;
  if (
    !vaProviderType ||
    !PROVIDERS_SUPPORTING_SCHEDULED_PAYOUTS.includes(vaProviderType)
  )
    return false;
  if (account.forCurrency == "EUR") return false;
  if (account.isLimitedVirtualAccount) return false;
  if (account.virtualAccountInformation.availableBalance === null) return false;
  if (account.virtualAccountInformation.balance === null) return false;
  return true;
};

export const hasDefaultDestinationAccount = (
  account: PayinAccountsWithAggregatedBalancesResponse["accounts"][number],
) =>
  Boolean(account.virtualAccountInformation.modulrDefaultPayoutBeneficiaryId);
